<script setup lang="ts">
/*
* Компонент DateRangeFilterCard — карточка фильтра по диапазону дат
*
* Основные функции:
* - Два текстовых поля: "дата с" и "дата по" (формат DD.MM.YYYY)
* - VDatePicker при клике на поле или иконку календаря
* - Пресеты быстрого выбора периодов (Сегодня, Вчера, Текущая/Прошлая неделя, Текущий/Прошлый месяц)
* - Кнопка "Применить" для подтверждения выбора
*
* Особенности:
* - Поле "дата по" заблокировано пока не заполнено поле "дата с"
* - Кнопка "Применить" заблокирована пока оба поля пусты
* - При выборе пресета заполняются оба поля автоматически
* - Компонент не управляет применением фильтра — эмитит событие 'apply' наружу
*
* Использование:
* <DateRangeFilterCard
*   :date-after="tempValues['created_after']"
*   :date-before="tempValues['created_before']"
*   @update:date-after="tempValues['created_after'] = $event"
*   @update:date-before="tempValues['created_before'] = $event"
*   @apply="applyDateFilter()"
* />
*/

import { ref } from 'vue'

// Определение входных параметров компонента
const props = defineProps<{
  // Значение поля "дата с" (начало периода, формат DD.MM.YYYY)
  dateAfter: string
  // Значение поля "дата по" (конец периода, формат DD.MM.YYYY)
  dateBefore: string
}>()

// Определение событий компонента
const emit = defineEmits<{
  // Обновление значения "дата с"
  'update:dateAfter': [value: string]
  // Обновление значения "дата по"
  'update:dateBefore': [value: string]
  // Сигнал к применению фильтра в родительском компоненте
  'apply': []
}>()

// Состояние открытия VMenu с VDatePicker для каждого поля
const dateFromMenu = ref(false)
const dateToMenu = ref(false)

// Форматирует объект Date в строку DD.MM.YYYY для отображения в инпуте
const formatDateForInput = (date: Date | null): string => {
  if (!date) return ''
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

// Конвертирует DD.MM.YYYY → Date (для VDatePicker)
const parseDdMmYyyy = (str: string): Date | undefined => {
  if (!str) return undefined
  const [day, month, year] = str.split('.').map(Number)
  if (!day || !month || !year) return undefined
  return new Date(year, month - 1, day)
}

// Конвертирует значение из VDatePicker (Date | string) → DD.MM.YYYY
const pickerValueToDisplay = (val: unknown): string => {
  if (!val) return ''
  if (val instanceof Date) return formatDateForInput(val)
  // VDatePicker может вернуть строку ISO (YYYY-MM-DD)
  if (typeof val === 'string') {
    const d = new Date(val)
    return isNaN(d.getTime()) ? '' : formatDateForInput(d)
  }
  return ''
}

// Обработчик выбора даты "с" из VDatePicker
const onDateFromSelect = (val: unknown) => {
  emit('update:dateAfter', pickerValueToDisplay(val))
  dateFromMenu.value = false
}

// Обработчик выбора даты "по" из VDatePicker
const onDateToSelect = (val: unknown) => {
  emit('update:dateBefore', pickerValueToDisplay(val))
  dateToMenu.value = false
}

// Пресеты дат для быстрого выбора периодов
// Каждый пресет содержит id, label для отображения и функцию getRange(),
// которая возвращает { start, end } в формате DD.MM.YYYY
const datePresets = [
  {
    id: 'today',
    label: 'Сегодня',
    getRange: () => {
      const d = new Date(); d.setHours(0, 0, 0, 0)
      return { start: formatDateForInput(d), end: formatDateForInput(d) }
    },
  },
  {
    id: 'yesterday',
    label: 'Вчера',
    getRange: () => {
      const d = new Date(); d.setDate(d.getDate() - 1); d.setHours(0, 0, 0, 0)
      return { start: formatDateForInput(d), end: formatDateForInput(d) }
    },
  },
  {
    id: 'current_week',
    label: 'Текущая неделя',
    getRange: () => {
      const today = new Date(); today.setHours(0, 0, 0, 0)
      const day = today.getDay(); const diff = (day === 0 ? -6 : 1) - day
      const start = new Date(today); start.setDate(today.getDate() + diff)
      const end = new Date(start); end.setDate(start.getDate() + 6)
      return { start: formatDateForInput(start), end: formatDateForInput(end) }
    },
  },
  {
    id: 'last_week',
    label: 'Прошлая неделя',
    getRange: () => {
      const today = new Date(); today.setHours(0, 0, 0, 0)
      const day = today.getDay(); const diff = (day === 0 ? -6 : 1) - day
      const startOfThisWeek = new Date(today); startOfThisWeek.setDate(today.getDate() + diff)
      const start = new Date(startOfThisWeek); start.setDate(startOfThisWeek.getDate() - 7)
      const end = new Date(start); end.setDate(start.getDate() + 6)
      return { start: formatDateForInput(start), end: formatDateForInput(end) }
    },
  },
  {
    id: 'current_month',
    label: 'Текущий месяц',
    getRange: () => {
      const today = new Date()
      const start = new Date(today.getFullYear(), today.getMonth(), 1)
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      return { start: formatDateForInput(start), end: formatDateForInput(end) }
    },
  },
  {
    id: 'last_month',
    label: 'Прошлый месяц',
    getRange: () => {
      const today = new Date()
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      const end = new Date(today.getFullYear(), today.getMonth(), 0)
      return { start: formatDateForInput(start), end: formatDateForInput(end) }
    },
  },
]

// Обработчик клика по пресету — заполняет оба поля датами из выбранного пресета
const handlePresetClick = (preset: typeof datePresets[number]) => {
  const range = preset.getRange()
  emit('update:dateAfter', range.start)
  emit('update:dateBefore', range.end)
}
</script>

<template>
  <VCard class="date-filter-card">
    <VCardText class="pa-4">
      <!-- Поле "дата с" с VDatePicker -->
      <VMenu
        v-model="dateFromMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        location="bottom start"
        min-width="auto"
      >
        <template #activator="{ props: activatorProps }">
          <VTextField
            v-bind="activatorProps"
            :model-value="props.dateAfter"
            variant="outlined"
            label="дата с"
            density="compact"
            class="mb-3"
            append-inner-icon="ri-calendar-line"
            hide-details
            readonly
            @click="dateFromMenu = true"
            @click:append-inner.prevent.stop="dateFromMenu = true"
          />
        </template>
        <VDatePicker
          :model-value="parseDdMmYyyy(props.dateAfter)"
          hide-header
          @update:model-value="onDateFromSelect"
        />
      </VMenu>

      <!-- Поле "дата по" с VDatePicker -->
      <VMenu
        v-model="dateToMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        location="bottom start"
        min-width="auto"
      >
        <template #activator="{ props: activatorProps }">
          <VTextField
            v-bind="activatorProps"
            :model-value="props.dateBefore"
            variant="outlined"
            label="дата по"
            density="compact"
            class="mb-4"
            append-inner-icon="ri-calendar-line"
            hide-details
            readonly
            :disabled="!props.dateAfter"
            @click="dateToMenu = true"
            @click:append-inner.prevent.stop="dateToMenu = true"
          />
        </template>
        <VDatePicker
          :model-value="parseDdMmYyyy(props.dateBefore)"
          hide-header
          @update:model-value="onDateToSelect"
        />
      </VMenu>

      <!-- Пресеты быстрого выбора периодов -->
      <div class="d-flex flex-wrap gap-2 mb-4">
        <VChip
          v-for="preset in datePresets"
          :key="preset.id"
          class="preset-chip body-1"
          size="small"
          @click="handlePresetClick(preset)"
        >
          {{ preset.label }}
        </VChip>
      </div>

      <!-- Кнопка применения фильтра -->
      <div>
        <VBtn
          variant="elevated"
          size="small"
          block
          :disabled="!(props.dateAfter || props.dateBefore)"
          @click="emit('apply')"
        >
          Применить
        </VBtn>
      </div>
    </VCardText>
  </VCard>
</template>
